import type { FunctionComponent } from "react";
import { Suspense, useEffect, useRef } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { Edition, useEdition } from "shared/lib/edition";

import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import type { headerOnelineMenuContainerQuery } from "scmp-app/queries/__generated__/headerOnelineMenuContainerQuery.graphql";

import { HeaderOnelineMenu } from "./header-oneline-menu";
import { HeaderOnelineMenuStyle } from "./header-oneline-menu/header-oneline-menu-style";
import {
  AllMenuContainer,
  Container,
  Divider,
  FallbackAllButton,
  StyledHeaderAllMenu,
} from "./styles";

export const query = graphql`
  query headerOnelineMenuContainerQuery($topicQueueName: String) {
    ...headerOnelineMenuStyleQuery
    ...headerAllMenuQuery
  }
`;

export type Props = {
  appBarVariant?: AppBarVariant;
  className?: string;
  initEdition?: Edition;
  isShowFullList?: boolean;
  isShowPrefixHyphen?: boolean;
};

export const HeaderOnelineMenuContainer: FunctionComponent<Props> = ({
  appBarVariant,
  className,
  initEdition,
  isShowFullList = true,
  isShowPrefixHyphen = true,
}) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<headerOnelineMenuContainerQuery>(query);
  const { get: getEdition } = useEdition({ initializeWithValue: true });

  useEffect(() => {
    const topicQueueMap = {
      [Edition.Asia]: "related_topic_homepage_asia_",
      [Edition.HongKong]: "related_topic_homepage_hk_",
      [Edition.International]: "related_topic_homepage_int_",
    };
    loadQuery({ topicQueueName: topicQueueMap[getEdition()] });
    return () => {
      disposeQuery();
    };
  }, [loadQuery, disposeQuery, getEdition]);

  const handleRenderOnelineMenu = () => {
    if (!isShowFullList) return null;

    switch (appBarVariant) {
      case "scmp/magazines-style":
        if (!queryReference) return null;
        return <HeaderOnelineMenuStyle queryReference={queryReference} />;
      default:
        return <HeaderOnelineMenu initEdition={initEdition} />;
    }
  };

  const handleRenderAllMenu = () => {
    if (!queryReference) return <FallbackAllButton />;
    return (
      <StyledHeaderAllMenu
        containerReference={containerReference}
        queryReference={queryReference}
      />
    );
  };

  return (
    <Container $variant={appBarVariant} className={className} ref={containerReference}>
      {handleRenderOnelineMenu()}
      <AllMenuContainer>
        {appBarVariant !== "scmp/magazines-style" && isShowPrefixHyphen && (
          <Divider $isShowFullList={isShowFullList}>-</Divider>
        )}
        <Suspense>{handleRenderAllMenu()}</Suspense>
      </AllMenuContainer>
    </Container>
  );
};

HeaderOnelineMenuContainer.displayName = "HeaderOnelineMenuContainer";
